import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'
import classNames from 'classnames'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Form,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Media,
  Row
} from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCertificate,
  faComments,
  faMapMarkerAlt,
  faSearch,
  faShieldAlt,
  faTags,
  faThLarge,
  faTimes,
  faToolbox
} from '@fortawesome/free-solid-svg-icons'
import Loader from '../../common/Loader'
import Avatar from '../../common/Avatar'
import Pagination from '../../common/pagination/Pagination'
import useSWRFetch from '../../../helpers/hooks/useSWRFetch'
import ServicePreviewCard from '../ServicePreviewCard'
import { getFeaturedProviders, getMarketplaceCategories, getMarketplaceServices } from '../../../helpers/api/marketplace'
import { categoryIcon, providerCardData, providerLocation, storageUrl } from '../marketplaceConstants'

const FILTER_KEYS = ['category', 'provider', 'price_min', 'price_max', 'search', 'page']
const PER_PAGE = 12

const readFilters = (search) => {
  const params = new URLSearchParams(search)
  return FILTER_KEYS.reduce((acc, key) => ({ ...acc, [key]: params.get(key) || '' }), {})
}

const toQuery = (filters) => {
  const params = new URLSearchParams()
  FILTER_KEYS.forEach((key) => {
    if (filters[key]) params.set(key, filters[key])
  })
  return params.toString()
}

const MarketplaceBanner = () => (
  <Card className="mb-3 overflow-hidden border-0">
    <CardBody
      className="text-white py-4 px-4 px-lg-5"
      style={{ background: 'linear-gradient(120deg, #5b1883 0%, #9b27c7 100%)' }}
    >
      <Row className="align-items-center">
        <Col lg={8}>
          <h3 className="text-white mb-2">Servicios para tu hogar, con el Sello Homelan</h3>
          <p className="mb-3" style={{ opacity: 0.85 }}>
            Encuentra plomeros, electricistas, técnicos de refrigeración y más. Todos los proveedores pasaron la
            verificación de Homelan. Pide una visita o una cotización sin salir de la app.
          </p>
          <Button tag={Link} to="/dashboard/marketplace/requests" color="light" size="sm" className="text-primary font-weight-semi-bold">
            <FontAwesomeIcon icon={faComments} className="mr-1" />
            Mis solicitudes
          </Button>
        </Col>
        <Col lg={4} className="d-none d-lg-flex justify-content-end" style={{ gap: 16 }}>
          {[
            { icon: faShieldAlt, label: 'Verificados' },
            { icon: faTags, label: 'Precios a la vista' }
          ].map((item) => (
            <div key={item.label} className="text-center">
              <div
                className="d-flex align-items-center justify-content-center rounded-circle bg-white text-primary mx-auto mb-1"
                style={{ width: 52, height: 52 }}
              >
                <FontAwesomeIcon icon={item.icon} size="lg" />
              </div>
              <span className="fs--1 font-weight-semi-bold">{item.label}</span>
            </div>
          ))}
        </Col>
      </Row>
    </CardBody>
  </Card>
)

const SectionHeader = ({ title, children }) => (
  <CardHeader className="bg-light d-flex flex-wrap align-items-center" style={{ gap: 8 }}>
    <h5 className="mb-0 mr-auto">{title}</h5>
    {children}
  </CardHeader>
)

const CategoryTile = ({ icon, label, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className="d-flex flex-column align-items-center text-center border-0 bg-transparent rounded-lg px-1 py-2"
    style={{ flex: '1 0 72px', maxWidth: 128 }}
  >
    <span
      className={classNames('d-flex align-items-center justify-content-center rounded-circle mb-2', active ? 'bg-primary text-white shadow-sm' : 'bg-soft-primary text-primary')}
      style={{ width: 52, height: 52, fontSize: 19 }}
    >
      <FontAwesomeIcon icon={icon} />
    </span>
    <span className={active ? 'text-primary font-weight-bold' : 'text-700 font-weight-semi-bold'} style={{ fontSize: 12, lineHeight: 1.25 }}>
      {label}
    </span>
  </button>
)

const CategoryGrid = ({ categories, active, onSelect }) => (
  <div className="d-flex justify-content-between" style={{ overflowX: 'auto' }}>
    <CategoryTile icon={faThLarge} label="Todas" active={!active} onClick={() => onSelect('')} />
    {categories.map((category) => (
      <CategoryTile
        key={category.id}
        icon={categoryIcon(category)}
        label={category.name}
        active={active === category.slug}
        onClick={() => onSelect(active === category.slug ? '' : category.slug)}
      />
    ))}
  </div>
)

const FeaturedProviderCard = ({ provider, selected, onSelect }) => {
  const card = providerCardData(provider)
  const location = providerLocation(provider)

  return (
    <button
      type="button"
      onClick={() => onSelect(provider)}
      className={classNames('d-block h-100 w-100 text-left rounded-lg border bg-white p-3', { 'border-primary': selected })}
    >
      <div>
        <Media className="align-items-center mb-2">
          <Avatar src={card.photoUrl} name={card.name} size="2xl" rounded={card.isCompany ? 'soft' : 'circle'} className="mr-3" />
          <Media body className="overflow-hidden">
            <h6 className="mb-0 text-truncate">
              {card.name}
              <FontAwesomeIcon icon={faCertificate} className="text-primary ml-1" title="Sello Homelan" />
            </h6>
            {location && (
              <p className="fs--1 text-600 mb-0 text-truncate">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-1" />
                {location}
              </p>
            )}
          </Media>
        </Media>
        <p className="fs--1 text-700 mb-2">
          {provider.active_services_count} {provider.active_services_count === 1 ? 'servicio publicado' : 'servicios publicados'}
        </p>
        <div className="d-flex flex-wrap" style={{ gap: 4 }}>
          {(provider.categories || []).map((category) => (
            <Badge key={category.id} color="soft-primary" pill>
              {category.name}
            </Badge>
          ))}
        </div>
      </div>
    </button>
  )
}

const ServicesFilters = ({ filters, onApply }) => {
  const [draft, setDraft] = useState({ search: filters.search, price_min: filters.price_min, price_max: filters.price_max })

  useEffect(() => {
    setDraft({ search: filters.search, price_min: filters.price_min, price_max: filters.price_max })
  }, [filters.search, filters.price_min, filters.price_max])

  const change = (field) => ({ target }) => setDraft((prev) => ({ ...prev, [field]: target.value }))

  return (
    <Form
      onSubmit={(event) => {
        event.preventDefault()
        onApply({ search: draft.search.trim(), price_min: draft.price_min, price_max: draft.price_max })
      }}
    >
      <Row form className="align-items-end">
        <Col md={5} className="mb-2">
          <InputGroup size="sm">
            <InputGroupAddon addonType="prepend">
              <InputGroupText>
                <FontAwesomeIcon icon={faSearch} />
              </InputGroupText>
            </InputGroupAddon>
            <Input value={draft.search} onChange={change('search')} placeholder="Busca un servicio o un proveedor" maxLength={100} />
          </InputGroup>
        </Col>
        <Col xs={6} md={2} className="mb-2">
          <InputGroup size="sm">
            <InputGroupAddon addonType="prepend">
              <InputGroupText>$</InputGroupText>
            </InputGroupAddon>
            <Input type="number" min="0" step="1" inputMode="decimal" value={draft.price_min} onChange={change('price_min')} placeholder="Desde" />
          </InputGroup>
        </Col>
        <Col xs={6} md={2} className="mb-2">
          <InputGroup size="sm">
            <InputGroupAddon addonType="prepend">
              <InputGroupText>$</InputGroupText>
            </InputGroupAddon>
            <Input type="number" min="0" step="1" inputMode="decimal" value={draft.price_max} onChange={change('price_max')} placeholder="Hasta" />
          </InputGroup>
        </Col>
        <Col md={3} className="mb-2">
          <Button type="submit" color="primary" size="sm" block>
            Filtrar
          </Button>
        </Col>
      </Row>
    </Form>
  )
}

const EmptyResults = ({ filtered, onClear }) => (
  <Card>
    <CardBody className="text-center py-5">
      <FontAwesomeIcon icon={faToolbox} size="3x" className="text-300 mb-3" />
      {filtered ? (
        <>
          <p className="text-600 mb-3">No encontramos servicios con estos filtros.</p>
          <Button color="falcon-default" size="sm" onClick={onClear}>
            Quitar filtros
          </Button>
        </>
      ) : (
        <>
          <h5 className="mb-2">Todavía no hay servicios publicados</h5>
          <p className="text-600 mb-0">Pronto vas a encontrar aquí a los proveedores verificados por Homelan.</p>
        </>
      )}
    </CardBody>
  </Card>
)

const MarketplaceHome = () => {
  const history = useHistory()
  const { search } = useLocation()
  const filters = useMemo(() => readFilters(search), [search])
  const servicesRef = useRef()

  const apiQuery = useMemo(() => {
    const params = new URLSearchParams(toQuery(filters))
    params.set('limit', PER_PAGE)
    return params.toString()
  }, [filters])

  const categoriesRequest = useSWRFetch('marketplace-categories', getMarketplaceCategories)
  const featuredRequest = useSWRFetch('marketplace-featured-providers', getFeaturedProviders)
  const servicesRequest = useSWRFetch(['marketplace-services', apiQuery], (key, query) => getMarketplaceServices(query))

  const categories = categoriesRequest.data?.data || []
  const featured = featuredRequest.data?.data || []
  const services = servicesRequest.data?.data || []
  const meta = servicesRequest.data?.meta
  const isFiltered = Boolean(filters.category || filters.provider || filters.price_min || filters.price_max || filters.search)
  const selectedProvider =
    filters.provider &&
    (featured.find((provider) => String(provider.id) === filters.provider) ||
      services.find((service) => String(service.provider?.id) === filters.provider)?.provider)

  const applyFilters = (changes, scroll = false) => {
    history.push({ search: toQuery({ ...filters, page: '', ...changes }) })
    if (scroll && servicesRef.current) servicesRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const goToPage = (page) => {
    applyFilters({ page: page > 1 ? String(page) : '' }, true)
  }

  return (
    <>
      <MarketplaceBanner />

      <Card className="mb-3">
        <SectionHeader title="Categorías" />
        <CardBody>
          <CategoryGrid categories={categories} active={filters.category} onSelect={(category) => applyFilters({ category }, true)} />
        </CardBody>
      </Card>

      {featured.length > 0 && (
        <Card className="mb-3">
          <SectionHeader title="Proveedores destacados">
            <span className="fs--1 text-600">Verificados, con más servicios publicados</span>
          </SectionHeader>
          <CardBody className="pb-0">
            <Row>
              {featured.map((provider) => (
                <Col key={provider.id} md={6} xl={4} className="mb-3">
                  <FeaturedProviderCard
                    provider={provider}
                    selected={String(provider.id) === filters.provider}
                    onSelect={(item) => applyFilters({ provider: String(item.id) }, true)}
                  />
                </Col>
              ))}
            </Row>
          </CardBody>
        </Card>
      )}

      <div ref={servicesRef} />
      <Card className="mb-3">
        <SectionHeader title="Servicios">
          {meta && <span className="fs--1 text-600">{meta.total === 1 ? '1 servicio' : `${meta.total} servicios`}</span>}
        </SectionHeader>
        <CardBody className="pb-1">
          <ServicesFilters filters={filters} onApply={(changes) => applyFilters(changes)} />
          {(filters.provider || filters.category) && (
            <div className="d-flex flex-wrap mb-2" style={{ gap: 8 }}>
              {filters.provider && (
                <Badge color="soft-primary" pill className="d-flex align-items-center">
                  Servicios de {selectedProvider?.display_name || 'este proveedor'}
                  <FontAwesomeIcon icon={faTimes} className="ml-2 cursor-pointer" onClick={() => applyFilters({ provider: '' })} />
                </Badge>
              )}
              {filters.category && (
                <Badge color="soft-primary" pill className="d-flex align-items-center">
                  {categories.find((category) => category.slug === filters.category)?.name || filters.category}
                  <FontAwesomeIcon icon={faTimes} className="ml-2 cursor-pointer" onClick={() => applyFilters({ category: '' })} />
                </Badge>
              )}
            </div>
          )}
        </CardBody>
      </Card>

      {servicesRequest.error ? (
        <Alert color="danger">
          No pudimos cargar los servicios.
          <Button color="link" size="sm" className="ml-1 p-0 align-baseline" onClick={() => servicesRequest.mutate()}>
            Reintentar
          </Button>
        </Alert>
      ) : servicesRequest.isLoading ? (
        <Card>
          <Loader padding="py-5" />
        </Card>
      ) : services.length === 0 ? (
        <EmptyResults filtered={isFiltered} onClear={() => history.push({ search: '' })} />
      ) : (
        <>
          <Row>
            {services.map((service) => (
              <Col key={service.id} sm={6} lg={4} className="mb-3">
                <Link to={`/dashboard/marketplace/services/${service.id}`} className="text-decoration-none d-block h-100">
                  <ServicePreviewCard
                    className="h-100"
                    service={service}
                    coverUrl={storageUrl(service.photos?.[0]?.path)}
                    provider={providerCardData(service.provider)}
                  />
                </Link>
              </Col>
            ))}
          </Row>
          {meta && meta.last_page > 1 && (
            <div className="d-flex justify-content-center mb-3">
              <Pagination
                meta={{ currentPage: meta.current_page, lastPage: meta.last_page }}
                prevPage={() => goToPage(meta.current_page - 1)}
                nextPage={() => goToPage(meta.current_page + 1)}
                goToPage={goToPage}
              />
            </div>
          )}
        </>
      )}
    </>
  )
}

export default MarketplaceHome
